import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentConfigService } from '../../agent-config/agent-config.service';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';
import {
  AgentResponse,
  GetAgentResponseHistoryRequest,
  GetAgentResponseHistoryResponse
} from '../../../models';
import {
  asSavedAnswerMetadata,
  hasSourceReferences,
  isRegulationsAgent,
  normalizeRegulationsCitationList
} from '../../regulation-citation/regulation-citation.util';
import { extractFilePathsFromHistoryAnswer } from '../../ria-history-file-url';
import { extractRetrievalModeFromHistoryAnswer, normalizeRetrievalMode } from '../../../../../shared/utils/retrieval-mode.util';

/**
 * Get Agent Response History Service
 * 
 * Handles retrieval of paginated conversation history for a specific thread.
 * 
 * Features:
 * - Paginated response history retrieval for a thread
 * - UUID validation for thread_id
 * - Mock mode for development/testing
 * - Configurable via JSON files
 * - Comprehensive error handling and logging
 * - Request payload validation
 * 
 * API Endpoint: GET /api/agent/response_history
 * 
 * @example
 * ```typescript
 * const request: GetAgentResponseHistoryRequest = {
 *   thread_id: '550e8400-e29b-41d4-a716-446655440001',
 *   user_id: 'user_12345',
 *   tenant_id: 'tenant_001',
 *   page: 1,
 *   page_size: 100
 * };
 * 
 * this.getAgentResponseHistoryService.getResponseHistory(request)
 *   .subscribe(response => {
 *     if (response.status === 'success') {
 *       console.log('Responses:', response.data?.responses);
 *     }
 *   });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class GetAgentResponseHistoryService {
  private useMock: boolean = true;
  private baseUrl: string = '/api/agent';
  private endpointPath: string = '/response_history';

  constructor(
    private logger: LoggerService,
    private agentConfigService: AgentConfigService,
    private agentApiService: AgentApiService
  ) {
    // Load configuration values (AgentConfigService already loaded at app startup)
    this.loadConfigurationValues();
  }

  /**
   * Load configuration values from AgentConfigService
   * 
   * Note: AgentConfigService is already loaded during app initialization,
   * so we just extract the values we need. URL resolution is now handled by AgentApiService.
   */
  private loadConfigurationValues(): void {
    try {
      // Get API config for mock mode
      const apiConfig = this.agentConfigService.getApiConfig() || {};
      this.useMock = apiConfig?.useMock ?? true;
      
      // Keep fallback values for backward compatibility
      this.baseUrl = apiConfig?.baseUrl || '/api/agent';
      this.endpointPath = '/response_history';

      // Get the actual endpoint URL from AgentApiService configuration
      const configuredEndpoint = this.agentApiService.getApiUrlFromConfig('get_agent_response_history');
      
      this.logger.logInfo(
        `[GetAgentResponseHistory] Configuration loaded - useMock: ${this.useMock}, endpoint: ${configuredEndpoint || `${this.baseUrl}${this.endpointPath}`}`,
        'loadConfigurationValues'
      );
    } catch (error: any) {
      this.logger.logInfo('[GetAgentResponseHistory] Error loading configuration values, using defaults', 'loadConfigurationValues', error);
    }
  }

  /**
   * Get Agent Response History
   * 
   * Retrieves paginated conversation history for a specific thread
   * 
   * @param request - GetAgentResponseHistoryRequest containing filter parameters
   * @returns Observable<GetAgentResponseHistoryResponse>
   * 
   * @throws {ValidationError} When required fields are missing or invalid
   * @throws {ApiError} When API call fails
   */
  getResponseHistory(request: GetAgentResponseHistoryRequest, agentId?: string): Observable<GetAgentResponseHistoryResponse> {
    try {
      this.logger.logInfo(
        `[GetAgentResponseHistory] Getting response history for thread: ${request.thread_id}, page: ${request.page || 1}, agent: ${agentId || 'unknown'}`,
        'getResponseHistory'
      );

      // Validate request
      const validationError = this.validateRequest(request);
      if (validationError) {
        return of(validationError);
      }

      // Check if mock mode is enabled
      if (this.useMock) {
        this.logger.logInfo('[GetAgentResponseHistory] Using mock response', 'getResponseHistory');
        return of(this.getMockResponseForThreadAndPage(request.thread_id, request.page || 1));
      }

      // Make actual API call
      this.logger.logInfo('[GetAgentResponseHistory] Making API call', 'getResponseHistory');
      return this.makeApiCall(request, agentId);
    } catch (error: any) {
      this.logger.logInfo('[GetAgentResponseHistory] Error in getResponseHistory', 'getResponseHistory', error);
      return of(this.createErrorResponse('Internal error occurred', 'INTERNAL_ERROR'));
    }
  }

  /**
   * Get mock response for specific thread and page
   * 
   * @param threadId - Thread ID to retrieve
   * @param page - Page number to retrieve
   * @returns GetAgentResponseHistoryResponse
   */
  private getMockResponseForThreadAndPage(threadId: string, page: number): GetAgentResponseHistoryResponse {
    try {
      const mockConfig = this.agentConfigService.getMockResponses();
      
      // Map thread IDs to their mock response base keys
      const threadMockMap: Record<string, string> = {
        '550e8400-e29b-41d4-a716-446655440001': 'get_agent_response_history_thread_001',
        '550e8400-e29b-41d4-a716-446655440002': 'get_agent_response_history_thread_002'
      };
      
      // Get thread-specific mock response key
      const baseKey = threadMockMap[threadId];
      
      if (baseKey) {
        // Try to get page-specific mock first (for paginated threads)
        const pageKey = `${baseKey}_page${page}`;
        const pageMock = mockConfig?.responses?.[pageKey];
        
        if (pageMock) {
          this.logger.logInfo(
            `[GetAgentResponseHistory] Returning mock for thread ${threadId}, page ${page}`,
            'getMockResponseForThreadAndPage'
          );
          return pageMock;
        }
        
        // Fallback to non-paginated mock (for threads with single page)
        const threadMock = mockConfig?.responses?.[baseKey];
        
        if (threadMock) {
          this.logger.logInfo(
            `[GetAgentResponseHistory] Returning non-paginated mock for thread ${threadId}`,
            'getMockResponseForThreadAndPage'
          );
          return threadMock;
        }
      }
      
      // No mock found for this thread - return empty history to show new chat page
      this.logger.logInfo(
        `[GetAgentResponseHistory] No mock found for thread ${threadId}, returning empty history to show new chat page`,
        'getMockResponseForThreadAndPage'
      );
      return {
        status: 'success',
        data: {
          thread_id: threadId,
          thread_name: 'New Chat',
          tenant_id: 'tenant_001',
          user_id: 'user_12345',
          responses: [],
          pagination: {
            current_page: 1,
            total_pages: 1,
            total_responses: 0
          }
        }
      };
    } catch (error: any) {
      this.logger.logInfo(
        '[GetAgentResponseHistory] Error getting mock for thread',
        'getMockResponseForThreadAndPage',
        error
      );
      return this.createErrorResponse('Error retrieving mock data', 'MOCK_ERROR');
    }
  }

  /**
   * Make actual API call to backend using shared AgentApiService
   * 
   * @param request - GetAgentResponseHistoryRequest
   * @returns Observable<GetAgentResponseHistoryResponse>
   */
  private makeApiCall(request: GetAgentResponseHistoryRequest, agentId?: string): Observable<GetAgentResponseHistoryResponse> {
    try {
      // Get endpoint URL from agent-specific configuration
      const endpoint = this.agentApiService.getApiUrlFromConfig('get_agent_response_history', agentId) || 
                     `${this.baseUrl}${this.endpointPath}`;

      // Get the agent-specific base URL
      const customBaseUrl = this.agentApiService.getBaseUrlForEndpoint('get_agent_response_history', agentId);

      // Build request body for centralized conversation history
      const requestBody: any = {
        thread_id: request.thread_id,
        user_id: request.user_id,
        tenant_id: request.tenant_id
      };

      if (request.page !== undefined) {
        requestBody.page = request.page;
      }

      if (request.page_size !== undefined) {
        requestBody.page_size = request.page_size;
      }

      this.logger.logInfo(
        `[GetAgentResponseHistory] ${agentId || 'default'} – calling ${endpoint}`,
        'makeApiCall'
      );

      // Use AgentApiService POST with request body, passing agent-specific base URL
      return this.agentApiService.post<any>(endpoint, requestBody, undefined, customBaseUrl).pipe(
        map((response: any) => {
          let normalized: GetAgentResponseHistoryResponse;

          if (this.isCentralizedResponse(response)) {
            normalized = this.transformCentralizedResponse(response, request, agentId);
          } else {
            normalized = response;
          }

          this.logger.logInfo(
            `[GetAgentResponseHistory] API call successful - status: ${normalized.status}`,
            'makeApiCall'
          );
          return normalized;
        }),
        catchError((error: any) => {
          this.logger.logInfo('[GetAgentResponseHistory] API call failed', 'makeApiCall', error);
          return of(this.handleApiError(error));
        })
      );
    } catch (error: any) {
      this.logger.logInfo('[GetAgentResponseHistory] Error making API call', 'makeApiCall', error);
      return of(this.createErrorResponse('Failed to make API call', 'API_ERROR'));
    }
  }

  /**
   * Check if the response is from the centralized get_conversation_history API.
   * Centralized API returns data as a flat array with total/page/total_pages at root.
   */
  private isCentralizedResponse(response: any): boolean {
    return response?.status?.toLowerCase() === 'success' && Array.isArray(response?.data);
  }

  /**
   * Transforms a centralized get_conversation_history response into the standard
   * GetAgentResponseHistoryResponse shape.
   *
   * Centralized API format:
   *  - status: "Success" (capital S)
   *  - thread_name at root level
   *  - data: flat array of conversation items with message_id, answer (dict), feedback (array)
   *  - pagination fields (total, page, total_pages, page_size) at root level
   *
   * NOTE: The centralized API returns responses in display order (chronological).
   * Components apply .reverse() assuming newest-first from the API, so we pre-reverse
   * here to counteract it and preserve the backend's intended display order.
   */
  private transformCentralizedResponse(
    raw: any,
    request: GetAgentResponseHistoryRequest,
    agentId?: string
  ): GetAgentResponseHistoryResponse {
    try {
      const rawItems: any[] = Array.isArray(raw.data) ? raw.data : [];

      const responses = rawItems.map((item: any) => {
        let answerObj: AgentResponse['answer'] = { text: '' };
        let imageVisualization = '';

        if (item.answer && typeof item.answer === 'object') {
          answerObj = { text: item.answer.text || item.answer.answer || '' };
          const thinking = typeof item.answer.thinking === 'string' ? item.answer.thinking.trim() : '';
          if (thinking) {
            answerObj.thinking = thinking;
          }
          const filePaths = extractFilePathsFromHistoryAnswer(item.answer);
          if (filePaths.length > 0) {
            answerObj.file_path = filePaths;
          }
          if (item.answer.image_visualization) {
            if (Array.isArray(item.answer.image_visualization)) {
              imageVisualization = item.answer.image_visualization
                .map((img: any) => {
                  if (typeof img === 'string') return img.trim();
                  return img?.png_base64?.trim() || '';
                })
                .filter((b64: string) => !!b64)
                .map((b64: string) => b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`)
                .join(' ');
            } else if (typeof item.answer.image_visualization === 'string') {
              imageVisualization = item.answer.image_visualization;
            }
          }
        } else if (typeof item.answer === 'string') {
          answerObj = { text: item.answer };
        }

        const retrievalMode = normalizeRetrievalMode(extractRetrievalModeFromHistoryAnswer(item.answer));
        if (retrievalMode) {
          answerObj.retrieval_mode = retrievalMode;
        }

        // Extract feedback info - handles both array format and single object format
        let isLike: boolean | null = null;
        let comments = '';
        if (item.feedback) {
          if (Array.isArray(item.feedback) && item.feedback.length > 0) {
            const latestFeedback = item.feedback[item.feedback.length - 1];
            if (latestFeedback.feedback_data) {
              isLike = latestFeedback.feedback_data.like ?? latestFeedback.feedback_data.is_like ?? null;
              comments = latestFeedback.feedback_data.comments || latestFeedback.feedback_data.comment || '';
            }
          } else if (typeof item.feedback === 'object' && !Array.isArray(item.feedback)) {
            const feedbackData = item.feedback.feedback_data;
            if (feedbackData) {
              isLike = feedbackData.like ?? feedbackData.is_like ?? null;
              comments = feedbackData.comments || feedbackData.comment || '';
            }
          }
        }

        let sourceMetadata = item.answer?.source_metadata || item.source_metadata;
        if (isRegulationsAgent(agentId) && !hasSourceReferences(sourceMetadata?.references)) {
          const citations = normalizeRegulationsCitationList(
            asSavedAnswerMetadata(item.answer?.metadata)?.citations
          );
          if (citations.length > 0) {
            sourceMetadata = { ...sourceMetadata, references: citations };
          }
        }

        return {
          response_id: item.message_id || '',
          question: item.question || '',
          answer: answerObj,
          image_visualization: imageVisualization || undefined,
          updated_at: item.agent_response_updated_at || item.agent_response_created_at || item.answer?.updated_at || '',
          source_metadata: sourceMetadata,
          response_metadata: item.response_metadata,
          is_like: isLike,
          feedback: item.feedback,
          comments: comments || undefined
        };
      });

      return {
        status: 'success',
        data: {
          thread_id: raw.thread_id || request.thread_id,
          thread_name: raw.thread_name || '',
          tenant_id: request.tenant_id,
          user_id: request.user_id,
          responses: responses.reverse(),
          pagination: {
            current_page: raw.page ?? 1,
            total_pages: raw.total_pages ?? 1,
            total_responses: raw.total ?? responses.length
          }
        }
      };
    } catch (error: any) {
      this.logger.logInfo(
        '[GetAgentResponseHistory] Error transforming centralized response',
        'transformCentralizedResponse',
        error
      );
      return this.createErrorResponse('Failed to transform response', 'TRANSFORM_ERROR');
    }
  }

  /**
   * Validate request payload
   * 
   * Validates:
   * - Required fields: thread_id, user_id, tenant_id
   * - thread_id must be valid UUID format
   * - page must be positive integer if provided
   * - page_size must be between 1 and 1000 if provided
   * 
   * @param request - GetAgentResponseHistoryRequest
   * @returns GetAgentResponseHistoryResponse | null (null if valid)
   */
  private validateRequest(request: GetAgentResponseHistoryRequest): GetAgentResponseHistoryResponse | null {
    try {
      // Check required fields
      if (!request.thread_id || request.thread_id.trim() === '') {
        return this.createErrorResponse('thread_id is required', 'VALIDATION_ERROR', {
          thread_id: 'Field is required'
        });
      }

      if (!request.user_id || request.user_id.trim() === '') {
        return this.createErrorResponse('user_id is required', 'VALIDATION_ERROR', {
          user_id: 'Field is required'
        });
      }

      if (!request.tenant_id || request.tenant_id.trim() === '') {
        return this.createErrorResponse('tenant_id is required', 'VALIDATION_ERROR', {
          tenant_id: 'Field is required'
        });
      }

      // Validate UUID format for thread_id
      if (!this.isValidUUID(request.thread_id)) {
        return this.createErrorResponse('thread_id must be a valid UUID', 'VALIDATION_ERROR', {
          thread_id: 'Invalid UUID format'
        });
      }

      // Validate optional pagination parameters
      if (request.page !== undefined && (request.page < 1 || !Number.isInteger(request.page))) {
        return this.createErrorResponse('page must be a positive integer', 'VALIDATION_ERROR', {
          page: 'Must be a positive integer'
        });
      }

      if (request.page_size !== undefined) {
        if (!Number.isInteger(request.page_size) || request.page_size < 1 || request.page_size > 1000) {
          return this.createErrorResponse('page_size must be between 1 and 1000', 'VALIDATION_ERROR', {
            page_size: 'Must be between 1 and 1000'
          });
        }
      }

      return null; // Validation passed
    } catch (error: any) {
      this.logger.logInfo('[GetAgentResponseHistory] Error validating request', 'validateRequest', error);
      return this.createErrorResponse('Validation error', 'VALIDATION_ERROR');
    }
  }

  /**
   * Validate UUID format
   * 
   * Checks if a string is a valid UUID format (any version: v1–v7).
   * The backend may generate UUIDs of any version (e.g. v4, v6),
   * so we only enforce the 8-4-4-4-12 hex structure.
   * 
   * @param uuid - String to validate
   * @returns boolean
   */
  private isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  /**
   * Handle API errors
   * 
   * Maps HTTP error codes to appropriate error responses
   * 
   * @param error - HTTP error
   * @returns GetAgentResponseHistoryResponse
   */
  private handleApiError(error: any): GetAgentResponseHistoryResponse {
    try {
      if (error.status === 400) {
        return this.createErrorResponse(
          error.error?.message || 'Invalid request parameters',
          'VALIDATION_ERROR',
          error.error?.details
        );
      }

      if (error.status === 401) {
        return this.createErrorResponse('Authentication required', 'UNAUTHORIZED');
      }

      if (error.status === 403) {
        return this.createErrorResponse('Access denied for tenant', 'FORBIDDEN');
      }

      if (error.status === 404) {
        return this.createErrorResponse('Thread not found', 'THREAD_NOT_FOUND');
      }

      if (error.status === 500) {
        return this.createErrorResponse('Internal server error', 'INTERNAL_ERROR');
      }

      return this.createErrorResponse(
        error.error?.message || 'An unexpected error occurred',
        error.error?.code || 'UNKNOWN_ERROR'
      );
    } catch (e: any) {
      this.logger.logInfo('[GetAgentResponseHistory] Error handling API error', 'handleApiError', e);
      return this.createErrorResponse('Failed to handle error', 'ERROR_HANDLING_ERROR');
    }
  }

  /**
   * Create error response
   * 
   * @param message - Error message
   * @param code - Error code
   * @param details - Optional error details
   * @returns GetAgentResponseHistoryResponse
   */
  private createErrorResponse(
    message: string,
    code: string,
    details?: Record<string, string>
  ): GetAgentResponseHistoryResponse {
    const response: GetAgentResponseHistoryResponse = {
      status: 'error',
      message,
      code
    };

    if (details) {
      response.details = details;
    }

    return response;
  }

  /**
   * Check if service is configured to use mock data
   * 
   * @returns boolean
   */
  public isUsingMock(): boolean {
    return this.useMock;
  }

  /**
   * Get current API endpoint
   * 
   * @returns string
   */
  public getEndpoint(): string {
    return this.agentApiService.getApiUrlFromConfig('get_agent_response_history') || 
           `${this.baseUrl}${this.endpointPath}`;
  }
}

